import { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Button, Container, Divider, FilledInput, Link, Paper, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from '@mui/material';
import PolarisIcon, { IconDictionary } from '../PolarisIcon';
import { useToast } from '../Toast';
import SlideToggle from '../SlideToggle';
import ClickCopy from '../ClickCopy/ClickCopy';

const IconLab = () => {
  const [textFilter, setTextFilter] = useState('');
  const [hoveredIcon, setHoveredIcon] = useState<string | null>(null);
  const [dimIcons, setDimIcons] = useState(false);
  const { success } = useToast();
  const [view, setView] = useState<string | boolean>(true);

  const filteredIconKeys = useMemo(() => {
    return Object.entries(IconDictionary)
      .filter(([key, value]) =>
        key.toLowerCase().includes(textFilter.toLowerCase()) || typeof value === 'string'
          ? (value as string).toLowerCase().includes(textFilter.toLowerCase())
          : false
      )
      .map(([key]) => key as string);
  }, [textFilter]);

  const copyToClipboard = (iconName: string) => {
    navigator.clipboard.writeText(iconName).then(() => {
      success(`Copied "${iconName}" to clipboard`);
    });
  };

  const openIconify = () => {
    window.open('https://icon-sets.iconify.design/', '_blank');
  };

  console.log('view', view);
  return (
    <Container>
      <HeaderDiv>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <StyledIconifyLogo icon="line-md:iconify2-static-twotone" height={80} onClick={openIconify} />
          <div>
            <h1>Icon Lab</h1>
            <p>
              Browse all standardized icons used in Polaris, hover to view each icon&#39;s assigned Polaris key and its corresponding Iconify
              reference. For more details on how icons are managed, see the{' '}
              <Link href="https://tmtinsights.atlassian.net/wiki/external/YmIyNzU1NTliZTY0NGFiMjgwM2U0M2YzMzRiZjE1NDc" target="_blank">
                Polaris Iconify Documentation.
              </Link>
            </p>
          </div>
        </div>
        <div>
          <Button onClick={openIconify} variant="outlined" size="small" sx={{ marginLeft: 4, textWrap: 'nowrap' }}>
            <PolarisIcon icon="external_link" height={20} />
            <span style={{ marginLeft: 5 }}>Open Iconify</span>
          </Button>
        </div>
      </HeaderDiv>
      <Divider />
      <SubHeaderDiv>
        <SlideToggle
          left={{ label: 'Gallery View', value: true }}
          right={{ label: 'List View', value: false }}
          callback={(value) => {
            setView(value);
          }}
          defaultUnchecked
        />
        <StyledFilledInput
          size="small"
          type="text"
          value={textFilter}
          placeholder="Search for an icon..."
          onChange={(e) => setTextFilter(e.target.value)}
          endAdornment={
            <EndAdornmentIcon
              type={textFilter && textFilter.length ? 'close' : 'search'}
              icon={textFilter && textFilter.length ? 'close_filled' : 'search'}
              onClick={() => setTextFilter('')}
            />
          }
        />
      </SubHeaderDiv>
      {view ? (
        <IconsListDiv onMouseEnter={() => setDimIcons(true)} onMouseLeave={() => setDimIcons(false)}>
          {filteredIconKeys.length > 0 ? (
            filteredIconKeys.map((key) => (
              <Tooltip
                key={key}
                title={
                  <div style={{ display: 'flex', flexDirection: 'row', columnGap: 3 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 60, width: 60 }}>
                      <PolarisIcon icon={key} height={50} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', rowGap: 5 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'row', columnGap: 5 }}>
                        <Label>Polaris: </Label>
                        <StyledInput type="text" value={key} readOnly />
                        <StyledCopyButton
                          onClick={() => {
                            copyToClipboard(key);
                          }}
                        >
                          <PolarisIcon icon="clipboard" />
                        </StyledCopyButton>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'row', columnGap: 5 }}>
                        <Label>Iconify: </Label>
                        <StyledInput type="text" value={IconDictionary[key] as string} readOnly />
                        <StyledCopyButton
                          onClick={() => {
                            copyToClipboard(IconDictionary[key] as string);
                          }}
                        >
                          <PolarisIcon icon="clipboard" />
                        </StyledCopyButton>
                      </div>
                    </div>
                  </div>
                }
                placement="top"
                arrow
              >
                <StyledPolarisIcon
                  dim={+dimIcons}
                  active={+(hoveredIcon === key)}
                  key={key}
                  icon={key}
                  height={30}
                  onMouseEnter={() => setHoveredIcon(key)}
                  onMouseLeave={() => setHoveredIcon(null)}
                  onClick={() => {
                    copyToClipboard(key);
                  }}
                />
              </Tooltip>
            ))
          ) : (
            <div style={{ textAlign: 'center', marginTop: 20 }}>
              <p>No icons found</p>
            </div>
          )}
        </IconsListDiv>
      ) : (
        <TableContainer>
          <StyledTable>
            <colgroup>
              <col style={{ width: 80 }} />
              <col style={{ width: 200 }} />
              <col style={{ width: 250 }} />
            </colgroup>
            <StyledHeader>
              <TableRow>
                <TableCell>Polaris Icon</TableCell>
                <TableCell>Polaris Key</TableCell>
                <TableCell>Iconify Key</TableCell>
              </TableRow>
            </StyledHeader>
            <TableBody>
              {filteredIconKeys.length > 0 ? (
                filteredIconKeys.map((key) => (
                  <TableRow key={key}>
                    <TableCell>
                      <PolarisIcon icon={key} height={30} />
                    </TableCell>
                    <TableCell>
                      <ClickCopy copyText={key}>{key}</ClickCopy>
                    </TableCell>
                    <TableCell>
                      <ClickCopy copyText={IconDictionary[key] as string}>{IconDictionary[key] as string}</ClickCopy>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    No icons found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </StyledTable>
        </TableContainer>
      )}
    </Container>
  );
};

export default IconLab;

const HeaderDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 20px;
  width: 100%;
  margin-bottom: 20px;

  h1 {
    margin-left: 20px;
    margin-bottom: 0px;
    margin-top: 0px;
  }

  p {
    font-size: 1.2rem;
    margin-left: 20px;
    color: #666;
    margin-top: 0px;
    margin-bottom: 0px;
  }
`;

const SubHeaderDiv = styled.div`
  margin-top: 20px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: ${({ theme }) => theme.spacing(2)};
`;

const IconsListDiv = styled.div``;

const StyledFilledInput = styled(FilledInput)`
  border: 0;
  border-radius: 50px;
  transition: ease opacity 2000ms;

  input {
    padding: ${({ theme }) => theme.spacing(1.25, 2)};

    &:-webkit-autofill {
      transition: background-color 50000s ease-in-out 0s !important;
      -webkit-box-shadow: 0 0 0 0px transparent inset !important;
    }
  }

  &::placeholder {
    opacity: 0.7;
  }
  &:after,
  &:before {
    display: none;
    border: 0;
  }
`;

const EndAdornmentIcon = styled(PolarisIcon)<{ type: 'search' | 'close' }>`
  cursor: pointer;
  color: ${({ theme, type }) => (type === 'search' ? theme.palette.primary.main : 'inherit')};
  transition: ease opacity 200ms;
  width: 25px;
  height: 25px;
  opacity: ${({ type }) => (type === 'close' ? 0.3 : 0.7)};

  &:hover {
    opacity: ${({ type }) => (type === 'close' ? 0.5 : 1)};
    transition: ease opacity 200ms;
  }
`;

const StyledPolarisIcon = styled(PolarisIcon)<{ active: number; dim: number }>`
  opacity: ${({ dim, active }) => (active ? 1 : dim ? 0.5 : 1)};
  cursor: pointer;
  margin: ${({ theme }) => theme.spacing(0.5)};
  transition: ease opacity 200ms;
`;

const StyledInput = styled.input`
  border: 1px solid #ccc;
  width: 110px;
`;

const StyledCopyButton = styled.button`
  background: ${({ theme }) => theme.palette.primary.main};
  cursor: pointer;
  border: none;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: -5px;
  height: 100%;
  border-top-right-radius: 5px;
  border-bottom-right-radius: 5px;

  &:hover {
    opacity: 0.8;
  }

  svg {
    width: 20px;
    height: 15px;
    color: ${({ theme }) => theme.palette.text.primary};
  }
`;

const Label = styled.label`
  font-weight: bold;
  margin-right: 5px;
  width: 50px;
`;

const StyledIconifyLogo = styled(PolarisIcon)`
  cursor: pointer;
  transition: ease color 200ms;

  &:hover {
    color: ${({ theme }) => theme.palette.primary.main};
  }
`;
const StyledTable = styled(Table)`
  min-width: 650px;
`;

const TableContainer = styled(Paper)`
  margin-top: 20px;
`;

const StyledHeader = styled(TableHead)`
  background-color: ${({ theme }) => theme.palette.primary.main};
  height: 30px;
`;
